import type { DailyGoalHistoryDay } from "@consistency-circle/shared";

export function todayString(): string {
  return new Date().toISOString().slice(0, 10);
}

export function addDays(localDate: string, days: number): string {
  const d = new Date(`${localDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Mean completion percent over the last `window` days, counting only days where
 * something was tracked. Returns null when no day in the window was tracked.
 */
export function rollingConsistency(
  days: DailyGoalHistoryDay[],
  window: number,
): number | null {
  const tracked = days.slice(-window).filter((d) => d.totalCount > 0);
  if (tracked.length === 0) return null;
  return Math.round(
    tracked.reduce((acc, d) => acc + d.completionPercent, 0) / tracked.length,
  );
}

export function rolling7(days: DailyGoalHistoryDay[]): number | null {
  return rollingConsistency(days, 7);
}

export function rolling30(days: DailyGoalHistoryDay[]): number | null {
  return rollingConsistency(days, 30);
}

/**
 * Consistency over the last `window` days minus the prior `window` days, in
 * percentage points. Null when the history doesn't cover both periods or
 * either period has no tracked day.
 */
export function periodOverPeriodDelta(
  days: DailyGoalHistoryDay[],
  window: number,
): number | null {
  if (days.length < window * 2) return null;
  const current = rollingConsistency(days.slice(-window), window);
  const previous = rollingConsistency(days.slice(-window * 2, -window), window);
  if (current === null || previous === null) return null;
  return current - previous;
}

/**
 * This week's consistency (last 7 days) minus the prior week's, in percentage
 * points. Null until both weeks have at least one tracked day.
 */
export function weekOverWeekDelta(days: DailyGoalHistoryDay[]): number | null {
  return periodOverPeriodDelta(days, 7);
}
